import { FC, useContext } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from '@wix/da-shared-react/pkg/Stream';
import { getDeviationsByIds } from '@wix/da-shared-react/pkg/redux/deviations/selectors';
import TierContentFeed from './TierContentFeed';
import TierContentToolbar from './TierContentToolbar';
import PostsSlider from './PostsSlider';
import EmptyState from './EmptyState';
import { getSelectedOrFirstTier } from '../../../../../selectors/subscriptionsSection';
import { isCurrentUserProfileOwner, getProfileOwner } from '../../../../../selectors/users';
import {
  getTierContentDeviationsStreamId,
  TIER_CONTENT_POSTS_PER_FETCH,
} from '../../../../../streams/tierContent';
import { SubscriptionsTierContentType } from '../../../../../../types/subscriptionsSection';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './TierContent.scss';

export interface Props {
  moduleId: number;
}

const TierContent: FC<Props> = () => {
  const tier = useSelector(getSelectedOrFirstTier);
  const isOwner = useSelector(isCurrentUserProfileOwner);
  const profileOwner = useSelector(getProfileOwner);
  const isMobile = useContext(MobileContext);

  // Get stream IDs for both content types
  const deviationsStreamId = getTierContentDeviationsStreamId(
    tier ? tier.deviationId : 0,
    SubscriptionsTierContentType.DEVIATIONS
  );
  const postsStreamId = getTierContentDeviationsStreamId(
    tier ? tier.deviationId : 0,
    SubscriptionsTierContentType.POSTS
  );

  // Get posts data from stream
  const normalizedPosts = useSelector(
    (state: any) => selectors.getItems(state, postsStreamId) || []
  );
  const posts = useSelector((state: any) => getDeviationsByIds(state, normalizedPosts));

  const deviationIds = useSelector(
    (state: any) => selectors.getItems(state, deviationsStreamId) || []
  );

  const isDeviationsStreamInitialized = useSelector((state: any) =>
    selectors.isInitialized(state, deviationsStreamId)
  );

  if (!tier) {
    return null;
  }

  const isUserSubscribed = tier.tier?.isUserSubscribed || false;
  const canViewPosts = isOwner || isUserSubscribed;
  const showPostsSlider = tier.isPublished && posts.length > 0 && canViewPosts;
  const showDeviationsContent = tier.isPublished;

  const showOwnerEmptyState =
    tier.isPublished &&
    isOwner &&
    posts.length === 0 &&
    isDeviationsStreamInitialized &&
    deviationIds.length === 0;

  return (
    <div className={s['root']}>
      {!isMobile && <TierContentToolbar />}
      <div className={s['content']}>
        {!tier.isPublished && <EmptyState isDraft tier={tier} />}
        {showOwnerEmptyState && <EmptyState isDraft={false} tier={tier} />}
        {showPostsSlider && (
          <div className={s['posts-section']}>
            <PostsSlider
              posts={posts}
              profileOwnerUsername={profileOwner.user.username}
              itemsPerFetch={TIER_CONTENT_POSTS_PER_FETCH}
            />
          </div>
        )}
        {showDeviationsContent && (
          <div className={s['deviations-section']}>
            <TierContentFeed
              tier={tier}
              streamId={deviationsStreamId}
              tierContentType={SubscriptionsTierContentType.DEVIATIONS}
            />
          </div>
        )}
      </div>
    </div>
  );
};
TierContent.displayName = 'TierContent';

export default TierContent;
